import { DoorSliding } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  TextField as MuiTextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { AutocompleteAvailabilityOption } from "../../../../components/shared/AutocompleteAvailabilityOption";
import { AutocompleteAvailabilityOptionDriver } from "../../../../components/shared/AutocompleteAvailabilityOptionDriver";
import { EquipmentDropdown } from "../../../../components/shared/EquipmentDropdown";
import { transformBackendResponse } from "../../../../components/shared/equipmentDropdown.utils";
import { GappedFlexColumn } from "../../../../components/shared/layout/Flex";
import Loading from "../../../../components/shared/layout/Loading";
import { TextField as TextFieldWithWarning } from "../../../../components/shared/layout/TextFieldWithWarning";
import { TextField } from "../../../../components/shared/layout/TextInput";
import { TimePicker } from "../../../../components/shared/TimePicker";
import { MAX_TRAP_NAME_LENGTH } from "../../../../constants/fieldNameLengthConstants";
import { useIsFeatureFlagEnabled } from "../../../../featureFlags/useIsFeatureFlagEnabled";
import { MergeIntoType } from "../../../../hooks/react-query/preplanning/useRouteTraps";
import { useDoorsBySicIdAndPlanDate } from "../../../../hooks/react-query/useDoors";
import { useDriversUsage } from "../../../../hooks/react-query/useDrivers";
import {
  useEquipmentBySicId,
  useEquipmentUsageBySicId,
  useGetEquipmentBySic,
} from "../../../../hooks/react-query/useEquipments";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { DropdownOption } from "../../../../types/dropdownOption.type";
import { equipmentTypeMap } from "../../../../types/equipment.type";
import { PlanTableFormat } from "../../../../types/planning/plan.type";
import { planningStatus } from "../../../../types/status.type";
import {
  blockSelectingAssignedDoorStatuses,
  EditedPlan,
  getValidationErrors,
} from "../../inboundPlanning/EditRouteConfirmDialog";
import { doorAvailability, getDoorOptionsFromDoors } from "../doorNumberUtils";
import { getDriverOptions } from "../driverUtils";
import {
  getStraightTruckOptions,
  getTractorOptions,
  getTrailerOptions,
  straightTruckAvailability,
  tractorAvailability,
  trailerAvailability,
} from "../equipmentUtils";
import {
  MergeDestination,
  MergeRoutesTrapsDialogAction,
  MergeRoutesTrapsDialogState,
} from "./MergeRoutesTrapsDialogReducer";

type MergeRoutesTrapsStepTwoProps = {
  validationErrors: ValidationErrors;
  mergeDestinationName: string;
  dialogState: MergeRoutesTrapsDialogState;
  dispatch: (value: MergeRoutesTrapsDialogAction) => void;
  showRouteEquipmentSelectors: boolean;
  plan: PlanTableFormat;
};

export type ValidationErrors = {
  name?: string;
  startTime?: string;
};

type MergeInfoNameAndDoorProps = {
  type: MergeIntoType;
  mergeDestination: MergeDestination;
  doorOptions: DropdownOption[];
  dispatch: (value: MergeRoutesTrapsDialogAction) => void;
  validationErrors: ValidationErrors;
  plan: PlanTableFormat;
};

type PlanningStatusArray = Array<keyof typeof planningStatus>;

const statusesBlockedFromSelection: string[] = [
  planningStatus.CLDK,
  planningStatus.CLDV,
  planningStatus.TRAP,
  planningStatus.DISP,
] as const satisfies PlanningStatusArray;

const MergeInfoNameAndDoor = ({
  type,
  dispatch,
  mergeDestination,
  doorOptions,
  validationErrors,
  plan,
}: MergeInfoNameAndDoorProps) => (
  <>
    <TextField
      sx={{ width: "100%" }}
      value={mergeDestination.customName}
      onChange={(event) =>
        dispatch({
          type: "set_customName",
          customName: event.target.value,
        })
      }
      id={`${type}-name`}
      placeholder={type === "trap" ? "Trap Name" : "Route Name"}
      inputProps={{ maxLength: MAX_TRAP_NAME_LENGTH }}
      error={validationErrors.name ? validationErrors.name.length > 0 : false}
      helperText={validationErrors.name}
    />
    <Autocomplete
      value={
        doorOptions.find((d) => Number(d.value) === mergeDestination.doorId) ??
        null
      }
      options={doorOptions}
      onChange={(_, option) => {
        dispatch({
          type: "set_doorId",
          doorId: Number(option?.value),
        });
      }}
      getOptionLabel={(door) => door.name}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select Door"
          label="Door Assignment"
        />
      )}
      groupBy={(option) => option.group || ""}
      getOptionDisabled={(option) =>
        blockSelectingAssignedDoorStatuses.includes(plan.status) &&
        option.group === doorAvailability.ASSIGNED
      }
      renderOption={(props, option) => (
        <AutocompleteAvailabilityOption
          key={option.name}
          listItemProps={props}
          name={option.name}
          assignment={option.assignment ?? null}
          leftIcon={<DoorSliding />}
        />
      )}
    />
  </>
);

export const MergeRoutesTrapsStepTwo = ({
  validationErrors,
  mergeDestinationName,
  dialogState,
  dispatch,
  showRouteEquipmentSelectors,
  plan,
}: MergeRoutesTrapsStepTwoProps) => {
  const isNewDropdownEnabled = useIsFeatureFlagEnabled(
    "operations-5722-listing-equipments-dropdown-client"
  );

  const [serviceCenter] = useSelectedServiceCenter();
  const { data: doors = [], isLoading: isLoadingDoors } =
    useDoorsBySicIdAndPlanDate(serviceCenter.id, plan.date);
  const doorOptions = getDoorOptionsFromDoors(doors);

  const {
    data: equipments = {
      tractors: [],
      trailers: [],
      straightTrucks: [],
    },
    isLoading: isLoadingEquipmentsLegacy,
  } = useEquipmentBySicId(serviceCenter.id, !isNewDropdownEnabled);

  const {
    data: equipmentsUsage = { tractors: [], trailers: [], straightTrucks: [] },
  } = useEquipmentUsageBySicId(
    serviceCenter.id,
    dialogState.mergeDestination.planDate,
    !isNewDropdownEnabled
  );

  const { data: equipmentBySic, isLoading: isLoadingEquipmentBySic } =
    useGetEquipmentBySic({
      sicId: serviceCenter.id,
      EquipmentType: [1, 2, 3],
      planDate: dialogState.mergeDestination.planDate,
      enabled: isNewDropdownEnabled,
    });

  const isLoadingEquipments = isNewDropdownEnabled
    ? isLoadingEquipmentBySic
    : isLoadingEquipmentsLegacy;

  const allOptions = equipmentBySic
    ? transformBackendResponse({
        ...equipmentBySic,
        assigned: [...equipmentBySic.assigned],
        available: [...equipmentBySic.available],
        loadAndHold: [...equipmentBySic.loadAndHold],
      })
    : [];
  const trailerOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 2
  );

  const tractorOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 1
  );

  const straightTruckOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 3
  );

  const { mergeIntoType, mergeDestination, equipmentTypeId } = dialogState;

  const { trailers, straightTrucks, tractors } = equipments;

  const { data: drivers = [] } = useDriversUsage(
    serviceCenter.id,
    mergeDestination.planDate
  );

  const driverOptions = getDriverOptions(drivers);

  const trailerOptions = getTrailerOptions(equipmentsUsage.trailers);

  const tractorOptions = getTractorOptions(equipmentsUsage.tractors);
  const straightTruckOptions = getStraightTruckOptions(
    equipmentsUsage.straightTrucks
  );

  const mergedPlanData: EditedPlan = useMemo(
    () => ({
      id: plan.id,
      routeEnd: plan.routeEnd,
      name: mergeDestination.customName || plan.name,
      startTime: mergeDestination.startTime
        ? dayjs(mergeDestination.startTime).toDate()
        : plan.startTime,
      endTime: plan.endTime,
      driverId: mergeDestination.driverId,
      doorId: mergeDestination.doorId,
      trailerEquipmentId: mergeDestination.trailerId,
      tractorEquipmentId: mergeDestination.tractorId,
      straightTruckEquipmentId: mergeDestination.straightTruckId,
    }),
    [
      plan.id,
      plan.routeEnd,
      plan.name,
      plan.startTime,
      plan.endTime,
      mergeDestination.customName,
      mergeDestination.startTime,
      mergeDestination.driverId,
      mergeDestination.doorId,
      mergeDestination.trailerId,
      mergeDestination.tractorId,
      mergeDestination.straightTruckId,
    ]
  );

  const trailerOrTractorValidationErrors = useMemo(
    () => getValidationErrors(plan, mergedPlanData),
    [plan, mergedPlanData]
  );

  const hasTrailers = isNewDropdownEnabled
    ? trailerOptionsBySic.length > 0
    : trailers.length > 0;
  const hasStraightTrucks = isNewDropdownEnabled
    ? straightTruckOptionsBySic.length > 0
    : straightTrucks.length > 0;

  useEffect(() => {
    if (equipmentTypeId === null && !isLoadingEquipments) {
      dispatch({
        type: "set_equipmentTypeId",
        equipmentTypeId: hasTrailers
          ? equipmentTypeMap.trailer
          : equipmentTypeMap.straightTruck,
      });
    }
  }, [dispatch, equipmentTypeId, hasTrailers, isLoadingEquipments]);

  const getSelectedStraightTruckTrailer = () => {
    if (mergeDestination.straightTruckId && mergeDestination.trailerId) {
      if (equipmentTypeId === equipmentTypeMap.trailer) {
        return trailerOptions.find(
          (d) => Number(d.value) === mergeDestination.trailerId
        );
      } else {
        return straightTruckOptions.find(
          (d) => Number(d.value) === mergeDestination.straightTruckId
        );
      }
    }
    return null;
  };

  const getSelectedTractor = () => {
    if (mergeDestination.tractorId) {
      const tractorAtMergeDestination = tractors.find(
        (e) => Number(e.id) === mergeDestination.tractorId
      );

      if (tractorAtMergeDestination) {
        return {
          name: tractorAtMergeDestination.name,
          value: String(tractorAtMergeDestination.id),
        };
      }

      throw new Error(
        `Tractor with id ${mergeDestination.tractorId} not found.`
      );
    }

    return null;
  };

  if (isLoadingDoors || isLoadingEquipments) {
    return <Loading />;
  }

  return (
    <Box px="24px" height={380} sx={{ overflow: "auto" }}>
      <GappedFlexColumn gap={0.7}>
        {mergeIntoType === "route" ? (
          <>
            Merging into {mergeDestinationName} route
            <MergeInfoNameAndDoor
              validationErrors={validationErrors}
              type={mergeIntoType}
              doorOptions={doorOptions}
              dispatch={dispatch}
              mergeDestination={mergeDestination}
              plan={plan}
            />
            <TimePicker
              label="Select Time"
              value={
                mergeDestination.startTime
                  ? dayjs(mergeDestination.startTime)
                  : null
              }
              onChange={(date) => {
                const isValidDate = date && date.isValid();
                dispatch({
                  type: "set_startTime",
                  startTime: isValidDate ? date.toISOString() : null,
                });
              }}
              slotProps={{
                textField: {
                  sx: { width: "100%" },
                  helperText: validationErrors.startTime,
                  error: validationErrors.startTime
                    ? validationErrors.startTime.length > 0
                    : false,
                },
              }}
            />
            <Autocomplete
              value={
                driverOptions.find(
                  (d) => d.value === mergeDestination.driverId
                ) ?? null
              }
              options={driverOptions}
              getOptionLabel={(driver) => `${driver.name} - ${driver.refCode}`}
              onChange={(_, newValue) => {
                dispatch({
                  type: "set_driverId",
                  driverId: newValue?.value ?? null,
                });
              }}
              renderInput={(params) => (
                <MuiTextField {...params} label="Driver" />
              )}
              groupBy={(option) => option.group}
              renderOption={(props, option) => (
                <AutocompleteAvailabilityOptionDriver
                  key={`${option.name}-${option.value}`}
                  listItemProps={props}
                  assignment={option.assignment}
                  name={option.name}
                  refCode={option.refCode}
                />
              )}
              getOptionDisabled={(option) =>
                statusesBlockedFromSelection.includes(
                  mergeDestination.status
                ) && option.group === doorAvailability.ASSIGNED
              }
            />
            <Box sx={{ marginTop: "16px" }}>
              <Typography fontSize={14} fontWeight={600} mb={1}>
                Select an equipment type
              </Typography>
            </Box>
            <Box sx={{ marginTop: "16px" }}>
              <ToggleButtonGroup
                color="primary"
                value={equipmentTypeId}
                exclusive
                aria-label="Platform"
                size="small"
              >
                <ToggleButton
                  value={equipmentTypeMap.trailer}
                  onChange={() => {
                    dispatch({
                      type: "set_equipmentTypeId",
                      equipmentTypeId: equipmentTypeMap.trailer,
                    });
                  }}
                >
                  Trailer
                </ToggleButton>
                <ToggleButton
                  value={equipmentTypeMap.straightTruck}
                  onChange={() => {
                    dispatch({
                      type: "set_equipmentTypeId",
                      equipmentTypeId: equipmentTypeMap.straightTruck,
                    });
                  }}
                >
                  Straight Truck
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box
              role="tabpanel"
              hidden={equipmentTypeMap.trailer !== equipmentTypeId}
            >
              <Box sx={{ marginTop: "16px" }}>
                {isNewDropdownEnabled ? (
                  <Stack spacing={1.5}>
                    <EquipmentDropdown
                      label="Trailer #"
                      equipmentTypeName="Trailer"
                      options={trailerOptionsBySic}
                      showSicCode={false}
                      value={
                        trailerOptionsBySic.find(
                          (o) => o.id === mergeDestination.trailerId
                        ) ?? null
                      }
                      getOptionDisabled={(option) =>
                        statusesBlockedFromSelection.includes(
                          mergeDestination.status
                        ) && !option.isAvailable
                      }
                      onChange={(option) =>
                        dispatch({
                          type: "set_trailerId",
                          trailerId: option ? option.id : null,
                        })
                      }
                    />
                    <EquipmentDropdown
                      label="Tractor #"
                      equipmentTypeName="Tractor"
                      options={tractorOptionsBySic}
                      showSicCode={false}
                      value={
                        tractorOptionsBySic.find(
                          (o) => o.id === mergeDestination.tractorId
                        ) ?? null
                      }
                      getOptionDisabled={(option) =>
                        statusesBlockedFromSelection.includes(
                          mergeDestination.status
                        ) && !option.isAvailable
                      }
                      onChange={(option) =>
                        dispatch({
                          type: "set_tractorId",
                          tractorId: option ? option.id : null,
                        })
                      }
                    />
                  </Stack>
                ) : (
                  <>
                    <Autocomplete
                      value={
                        trailerOptions.find(
                          (d) => Number(d.value) === mergeDestination.trailerId
                        ) ?? null
                      }
                      sx={{ mb: "10px" }}
                      options={trailerOptions}
                      getOptionDisabled={(option) =>
                        (statusesBlockedFromSelection.includes(
                          mergeDestination.status
                        ) &&
                          option.group === trailerAvailability.ASSIGNED) ||
                        option.group === trailerAvailability.INACTIVE
                      }
                      onChange={(_, newValue) => {
                        dispatch({
                          type: "set_trailerId",
                          trailerId: newValue?.value
                            ? Number(newValue.value)
                            : null,
                        });
                      }}
                      getOptionLabel={(trailer) => trailer.name}
                      renderInput={(params) => (
                        <TextFieldWithWarning
                          {...params}
                          label="Trailer #"
                          helperText={
                            trailerOrTractorValidationErrors.trailerOrStraightTruckEquipment
                          }
                          warning="Unavailable"
                        />
                      )}
                      groupBy={(option) => option.group}
                      renderOption={(props, option) => (
                        <AutocompleteAvailabilityOption
                          key={option.name}
                          listItemProps={props}
                          name={option.name}
                          assignment={option.assignment}
                        />
                      )}
                    />
                    <Autocomplete
                      value={
                        tractorOptions.find(
                          (d) => Number(d.value) === mergeDestination.tractorId
                        ) ?? null
                      }
                      sx={{ mb: "10px" }}
                      options={tractorOptions}
                      getOptionDisabled={(option) =>
                        (statusesBlockedFromSelection.includes(
                          mergeDestination.status
                        ) &&
                          option.group === tractorAvailability.ASSIGNED) ||
                        option.group === tractorAvailability.INACTIVE
                      }
                      onChange={(_, newValue) => {
                        dispatch({
                          type: "set_tractorId",
                          tractorId: newValue?.value
                            ? Number(newValue.value)
                            : null,
                        });
                      }}
                      getOptionLabel={(tractor) => tractor.name}
                      renderInput={(params) => (
                        <TextFieldWithWarning
                          {...params}
                          label="Tractor #"
                          helperText={
                            trailerOrTractorValidationErrors.tractorEquipment
                          }
                          warning="Unavailable"
                        />
                      )}
                      groupBy={(option) => option.group}
                      renderOption={(props, option) => (
                        <AutocompleteAvailabilityOption
                          key={option.name}
                          listItemProps={props}
                          name={option.name}
                          assignment={option.assignment}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            </Box>
            <Box
              role="tabpanel"
              hidden={equipmentTypeMap.straightTruck !== equipmentTypeId}
            >
              <Box sx={{ marginTop: "16px" }}>
                {isNewDropdownEnabled ? (
                  <EquipmentDropdown
                    label="ST #"
                    equipmentTypeName="Straight Truck"
                    options={straightTruckOptionsBySic}
                    showSicCode={false}
                    value={
                      straightTruckOptionsBySic.find(
                        (o) => o.id === mergeDestination.straightTruckId
                      ) ?? null
                    }
                    getOptionDisabled={(option) =>
                      statusesBlockedFromSelection.includes(
                        mergeDestination.status
                      ) && !option.isAvailable
                    }
                    onChange={(option) =>
                      dispatch({
                        type: "set_straightTruckId",
                        straightTruckId: option ? option.id : null,
                      })
                    }
                  />
                ) : (
                  <Autocomplete
                    value={
                      straightTruckOptions.find(
                        (d) =>
                          Number(d.value) === mergeDestination.straightTruckId
                      ) ?? null
                    }
                    options={straightTruckOptions}
                    onChange={(_, newValue) => {
                      dispatch({
                        type: "set_straightTruckId",
                        straightTruckId: newValue?.value ?? null,
                      });
                    }}
                    getOptionLabel={(trailer) => trailer.name}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="ST #" />
                    )}
                    groupBy={(option) => option.group}
                    getOptionDisabled={(option) =>
                      (statusesBlockedFromSelection.includes(
                        mergeDestination.status
                      ) &&
                        option.group === straightTruckAvailability.ASSIGNED) ||
                      option.group === straightTruckAvailability.INACTIVE
                    }
                    renderOption={(props, option) => (
                      <AutocompleteAvailabilityOption
                        key={option.name}
                        listItemProps={props}
                        name={option.name}
                        assignment={option.assignment}
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
          </>
        ) : (
          <>
            Merging into {mergeDestinationName} trap
            <MergeInfoNameAndDoor
              validationErrors={validationErrors}
              type={mergeIntoType}
              doorOptions={doorOptions}
              dispatch={dispatch}
              mergeDestination={mergeDestination}
              plan={plan}
            />
          </>
        )}
        {(mergeIntoType === "trap" || showRouteEquipmentSelectors) && (
          <>
            {hasTrailers && hasStraightTrucks && (
              <>
                <Box sx={{ marginTop: "16px" }}>
                  <Typography fontSize={14} fontWeight={600} mb={1}>
                    Select an equipment type
                  </Typography>
                </Box>
                <Box sx={{ marginTop: "16px" }}>
                  <ToggleButtonGroup
                    color="primary"
                    value={equipmentTypeId}
                    exclusive
                    aria-label="Platform"
                    size="small"
                  >
                    <ToggleButton
                      value={equipmentTypeMap.trailer}
                      onChange={() => {
                        dispatch({
                          type: "set_equipmentTypeId",
                          equipmentTypeId: equipmentTypeMap.trailer,
                        });
                      }}
                    >
                      Trailer
                    </ToggleButton>
                    <ToggleButton
                      value={equipmentTypeMap.straightTruck}
                      onChange={() => {
                        dispatch({
                          type: "set_equipmentTypeId",
                          equipmentTypeId: equipmentTypeMap.straightTruck,
                        });
                      }}
                    >
                      Straight Truck
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </>
            )}
            {equipmentTypeId !== null &&
              (isNewDropdownEnabled ? (
                <>
                  <EquipmentDropdown
                    label={
                      equipmentTypeMap.trailer === equipmentTypeId
                        ? "Trailer #"
                        : "ST #"
                    }
                    showSicCode={false}
                    equipmentTypeName={
                      equipmentTypeMap.trailer === equipmentTypeId
                        ? "Trailer"
                        : "Straight Truck"
                    }
                    options={
                      equipmentTypeId === equipmentTypeMap.trailer
                        ? trailerOptionsBySic
                        : straightTruckOptionsBySic
                    }
                    value={
                      equipmentTypeId === equipmentTypeMap.trailer
                        ? trailerOptionsBySic.find(
                            (o) => o.id === mergeDestination.trailerId
                          ) ?? null
                        : straightTruckOptionsBySic.find(
                            (o) => o.id === mergeDestination.straightTruckId
                          ) ?? null
                    }
                    getOptionDisabled={(option) =>
                      statusesBlockedFromSelection.includes(
                        mergeDestination.status
                      ) && !option.isAvailable
                    }
                    onChange={(option) => {
                      if (equipmentTypeId === equipmentTypeMap.trailer) {
                        dispatch({
                          type: "set_trailerId",
                          trailerId: option ? option.id : null,
                        });
                      } else {
                        dispatch({
                          type: "set_straightTruckId",
                          straightTruckId: option ? option.id : null,
                        });
                      }
                    }}
                  />
                  {showRouteEquipmentSelectors &&
                    equipmentTypeId === equipmentTypeMap.trailer && (
                      <EquipmentDropdown
                        label="Tractor"
                        equipmentTypeName="Tractor"
                        options={tractorOptionsBySic}
                        showSicCode={false}
                        value={
                          tractorOptionsBySic.find(
                            (o) => o.id === mergeDestination.tractorId
                          ) ?? null
                        }
                        getOptionDisabled={(option) =>
                          statusesBlockedFromSelection.includes(
                            mergeDestination.status
                          ) && !option.isAvailable
                        }
                        onChange={(option) =>
                          dispatch({
                            type: "set_tractorId",
                            tractorId: option ? option.id : null,
                          })
                        }
                      />
                    )}
                </>
              ) : (
                <>
                  <Autocomplete
                    value={getSelectedStraightTruckTrailer()}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    options={
                      equipmentTypeId === equipmentTypeMap.trailer
                        ? trailerOptions
                        : straightTruckOptions
                    }
                    onChange={(_, newValue) => {
                      if (equipmentTypeId === equipmentTypeMap.trailer) {
                        dispatch({
                          type: "set_trailerId",
                          trailerId: newValue?.value ?? null,
                        });
                      } else {
                        dispatch({
                          type: "set_straightTruckId",
                          straightTruckId: newValue?.value ?? null,
                        });
                      }
                    }}
                    getOptionLabel={(equipment) => equipment.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          equipmentTypeMap.trailer === equipmentTypeId
                            ? "Trailer #"
                            : "ST #"
                        }
                      />
                    )}
                    groupBy={(option) => option.group}
                    renderOption={(props, option) => (
                      <AutocompleteAvailabilityOption
                        key={option.name}
                        listItemProps={props}
                        name={option.name}
                        assignment={option.assignment}
                      />
                    )}
                  />
                  {showRouteEquipmentSelectors &&
                    equipmentTypeId === equipmentTypeMap.trailer && (
                      <Autocomplete
                        value={getSelectedTractor()}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        options={tractors.map(
                          ({ name, equipmentType, id }) => ({
                            name: name || `${equipmentType}-${id}`,
                            value: id.toString(),
                          })
                        )}
                        onChange={(_, option) => {
                          dispatch({
                            type: "set_tractorId",
                            tractorId: Number(option?.value),
                          });
                        }}
                        getOptionLabel={(equipment) => equipment.name}
                        renderInput={(params) => (
                          <MuiTextField {...params} label="Tractor" />
                        )}
                      />
                    )}
                </>
              ))}
            {!hasTrailers && !hasStraightTrucks && (
              <Typography>No equipment available yet</Typography>
            )}
          </>
        )}
      </GappedFlexColumn>
    </Box>
  );
};
